
import React, { useState } from 'react';
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useCropperLogic } from './cropper/useCropperLogic';
import CropperDialog from './cropper/CropperDialog';
import CropperCanvas from './cropper/CropperCanvas';
import CropperControls from './cropper/CropperControls';
import CropperPreview from './cropper/CropperPreview';

interface ProfileImageCropperProps {
  isOpen: boolean;
  onClose: () => void;
  onCropComplete: (croppedImageBlob: Blob, fileName: string) => void;
  imageFile: File | null;
}

const ProfileImageCropper = ({ isOpen, onClose, onCropComplete, imageFile }: ProfileImageCropperProps) => {
  const [processing, setProcessing] = useState(false);
  const [zoom, setZoom] = useState(1);

  const {
    crop,
    setCrop,
    completedCrop,
    setCompletedCrop,
    imageSrc,
    imgRef,
    canvasRef,
    onImageLoad,
    getCroppedImg,
    handleReset
  } = useCropperLogic({ imageFile, zoom });

  React.useEffect(() => {
    if (imageFile) {
      setZoom(1); // Reset zoom when new image is loaded
    }
  }, [imageFile]);

  const handleSaveCrop = async () => {
    if (!imageFile) return;
    
    setProcessing(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        onCropComplete(croppedBlob, imageFile.name);
        onClose();
        toast({
          title: "Square profile photo saved!",
          description: "Your cropped photo is ready for your profile.",
        });
      }
    } catch (error) {
      toast({
        title: "Cropping failed",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleResetWithZoom = () => {
    handleReset();
    setZoom(1);
  };

  return (
    <CropperDialog
      isOpen={isOpen}
      onClose={onClose}
      processing={processing}
      completedCrop={completedCrop}
      onReset={handleResetWithZoom}
      onSave={handleSaveCrop}
    >
      {imageSrc && (
        <div className="flex flex-col items-center space-y-4">
          <CropperCanvas
            imageSrc={imageSrc}
            crop={crop}
            zoom={zoom}
            setZoom={setZoom}
            setCrop={setCrop}
            setCompletedCrop={setCompletedCrop}
            onImageLoad={onImageLoad}
            imgRef={imgRef}
          />
          
          <CropperControls zoom={zoom} />
          
          <CropperPreview
            completedCrop={completedCrop}
            imageSrc={imageSrc}
            imgRef={imgRef}
            canvasRef={canvasRef}
            zoom={zoom}
          />
        </div>
      )}
    </CropperDialog>
  );
};

export default ProfileImageCropper;
